import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { StatCard } from "@/components/common/StatCard";
import { EmptyState } from "@/components/common/EmptyState";
import { WorkGroup } from "@/components/workspace/WorkItemRow";
import { WorkItemDialog } from "@/components/workspace/WorkItemDialog";
import { KpiPeriodCard } from "@/components/workspace/KpiPeriodCard";
import { ResourceList } from "@/components/workspace/ResourceList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MyLeaveTab } from "@/components/people/MyLeaveTab";
import { MyTimeTab } from "@/components/people/MyTimeTab";
import { ClockCard } from "@/components/people/ClockCard";
import { DAYS, weeklyHours } from "@/lib/workspace";
import { leaveOn, clockOn, fmtDays } from "@/lib/people";
import { fmtDate } from "@/lib/format";

const CLOSED = ["completed", "cancelled"];

export default function MyWorkspace() {
  const { orgId, currentOrg } = useOrg();
  const showLeave = leaveOn(currentOrg);
  const showTime = clockOn(currentOrg);
  const [leave, setLeave] = useState(null);
  const [clock, setClock] = useState(null);
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [work, setWork] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [resources, setResources] = useState([]);
  const [open, setOpen] = useState(null);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "work";
  const setTab = (t) => setParams({ tab: t }, { replace: true });

  const load = () => Promise.all([
    api.get(`/workspace/me?org_id=${orgId}`).then((r) => setMe(r.data)),
    api.get(`/workspace/my-work?org_id=${orgId}`).then((r) => setWork(r.data)),
    api.get(`/workspace/my-kpis?org_id=${orgId}`).then((r) => setKpis(r.data)),
    api.get(`/workspace/resources?org_id=${orgId}`).then((r) => setResources(r.data)).catch(() => setResources([])),
    showLeave && loadLeave(),
    showTime && loadClock(),
  ]);
  const loadLeave = () => api.get(`/workspace/leave/me?org_id=${orgId}`).then((r) => setLeave(r.data)).catch(() => setLeave(null));
  const loadClock = () => api.get(`/workspace/clock/me?org_id=${orgId}`).then((r) => setClock(r.data)).catch(() => setClock(null));
  useEffect(() => { if (orgId) load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const acts = work?.actions || [];
    const today = dayjs().format("YYYY-MM-DD");
    const sunday = dayjs().add((7 - dayjs().day()) % 7, "day").format("YYYY-MM-DD");
    const openActs = acts.filter((a) => !CLOSED.includes(a.status));
    return {
      overdue: openActs.filter((a) => a.due_date && a.due_date.slice(0, 10) < today && a.status !== "awaiting_review"),
      week: openActs.filter((a) => a.due_date && a.due_date.slice(0, 10) >= today && a.due_date.slice(0, 10) <= sunday && a.status !== "awaiting_review"),
      later: openActs.filter((a) => (!a.due_date || a.due_date.slice(0, 10) > sunday) && a.status !== "awaiting_review"),
      submitted: openActs.filter((a) => a.status === "awaiting_review"),
      done: acts.filter((a) => a.status === "completed").slice(-10).reverse(),
    };
  }, [work]);

  if (!work || !me) return <PageLoading />;
  const kpiDue = kpis.reduce((s, k) => s + k.periods.filter((p) => ["missing", "due"].includes(p.state)).length, 0);
  const toRead = resources.filter((r) => r.requires_acknowledgement && r.in_my_audience && !r.acknowledged).length;
  const reviews = work.reviews || [];
  const annual = leave?.balances?.find((b) => b.has_entitlement && b.year_label === (leave.today || "").slice(0, 4)) || leave?.balances?.find((b) => b.has_entitlement);

  return (
    <div data-testid="my-workspace-page">
      <PageHeader eyebrow={me.company_name} title="My Workspace" icon="Briefcase"
        subtitle={`Hi ${user?.name?.split(" ")[0]} — your work, the KPIs you report${showLeave ? ", your leave" : ""}${showTime ? ", your time" : ""} and what the company has shared with you.`} />

      {showTime && clock && tab !== "time" && <ClockCard orgId={orgId} data={clock} onChanged={loadClock} compact />}

      <div className={`mb-7 grid grid-cols-2 gap-3 ${annual ? "lg:grid-cols-6" : "lg:grid-cols-5"}`}>
        <StatCard icon="CalendarClock" label="Due this week" value={groups.week.length} testId="ws-stat-week" onClick={() => setTab("work")} />
        <StatCard icon="AlarmClock" label="Overdue" value={groups.overdue.length} tone={groups.overdue.length ? "red" : "green"} testId="ws-stat-overdue" onClick={() => setTab("work")} />
        <StatCard icon="ShieldCheck" label="With reviewer" value={groups.submitted.length} tone="amber" testId="ws-stat-review" onClick={() => setTab("work")} />
        <StatCard icon="Gauge" label="KPI updates due" value={kpiDue} tone={kpiDue ? "amber" : "green"} testId="ws-stat-kpis" onClick={() => setTab("kpis")} />
        <StatCard icon="BookOpen" label="To read" value={toRead} tone={toRead ? "blue" : "green"} testId="ws-stat-read" onClick={() => setTab("resources")} />
        {annual && <StatCard icon="Plane" label={`${annual.type_name} left`} value={fmtDays(annual.available, leave.day_hours)} tone="blue" testId="ws-stat-leave" onClick={() => setTab("leave")} />}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 flex h-auto flex-wrap justify-start bg-white">
          <TabsTrigger value="work" data-testid="ws-tab-work">My work</TabsTrigger>
          {reviews.length > 0 && <TabsTrigger value="reviews" data-testid="ws-tab-reviews">To review ({reviews.length})</TabsTrigger>}
          <TabsTrigger value="kpis" data-testid="ws-tab-kpis">My KPIs</TabsTrigger>
          {showLeave && <TabsTrigger value="leave" data-testid="ws-tab-leave">Leave</TabsTrigger>}
          {showTime && <TabsTrigger value="time" data-testid="ws-tab-time">Time</TabsTrigger>}
          <TabsTrigger value="resources" data-testid="ws-tab-resources">Resources</TabsTrigger>
          <TabsTrigger value="profile" data-testid="ws-tab-profile">My profile</TabsTrigger>
        </TabsList>

        <TabsContent value="work">
          {(work.actions || []).length === 0 ? (
            <EmptyState icon="ListChecks" title="Nothing assigned yet" description="When your manager assigns work linked to the business plan, it appears here." testId="ws-work-empty" />
          ) : <>
            <WorkGroup title="Overdue" items={groups.overdue} onOpen={setOpen} testId="ws-group-overdue" />
            <WorkGroup title="Due this week" items={groups.week} onOpen={setOpen} testId="ws-group-week" empty="Nothing else due this week." />
            <WorkGroup title="Coming up" items={groups.later} onOpen={setOpen} testId="ws-group-later" />
            <WorkGroup title="With your reviewer" items={groups.submitted} onOpen={setOpen} testId="ws-group-submitted" />
            <WorkGroup title="Recently completed" items={groups.done} onOpen={setOpen} testId="ws-group-done" />
          </>}
        </TabsContent>
        <TabsContent value="reviews">
          <WorkGroup title="Waiting for your independent review" items={reviews} onOpen={setOpen} showOwner testId="ws-group-reviews" />
        </TabsContent>
        <TabsContent value="kpis">
          {kpis.length === 0
            ? <EmptyState icon="Gauge" title="You don't report any KPIs" description="KPIs you own will appear here for each reporting period." testId="ws-kpis-empty" />
            : <div className="space-y-4">{kpis.map((k) => <KpiPeriodCard key={k.id} kpi={k} canRecord meId={user?.id} onChanged={load} />)}</div>}
        </TabsContent>
        {showLeave && (
          <TabsContent value="leave">
            {leave ? <MyLeaveTab orgId={orgId} data={leave} onChanged={loadLeave} /> : <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" />}
          </TabsContent>
        )}
        {showTime && (
          <TabsContent value="time">
            {clock ? <MyTimeTab orgId={orgId} data={clock} onChanged={loadClock} /> : <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" />}
          </TabsContent>
        )}
        <TabsContent value="resources">
          <ResourceList orgId={orgId} resources={resources} canAdmin={false} canManage={false} onChanged={load} />
        </TabsContent>
        <TabsContent value="profile"><ProfileCard profile={me.profile} /></TabsContent>
      </Tabs>

      {open && <WorkItemDialog actionId={open.id} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function ProfileCard({ profile }) {
  if (!profile) return <EmptyState icon="IdCard" title="No employment profile" description="Your administrator hasn't set up an employment profile for you yet." testId="ws-profile-empty" />;
  const rows = [
    ["Job title", profile.job_title], ["Department", profile.department], ["Manager", profile.manager_name],
    ["Employment type", (profile.employment_type || "").replace("_", "-")], ["Start date", fmtDate(profile.start_date)], ["Email", profile.email],
  ];
  return (
    <div className="amden-card p-6" data-testid="ws-profile">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map(([l, v]) => <div key={l}><div className="label-eyebrow mb-1">{l}</div><div className="text-sm text-[#0B1729]">{v || "—"}</div></div>)}
      </div>
      <div className="mt-6">
        <div className="label-eyebrow mb-2">Work pattern · {weeklyHours(profile.work_pattern)} hours a week</div>
        <div className="grid grid-cols-7 gap-1.5">
          {DAYS.map(([k, l]) => {
            const h = (profile.work_pattern || {})[k];
            return (
              <div key={k} className={`rounded-lg px-2 py-2.5 text-center ${h ? "bg-[#0B1729] text-white" : "bg-[#F4F6F9] text-[#1E2D44]/35"}`}>
                <div className="text-[11px]">{l}</div><div className="font-display text-sm font-bold">{h ? `${h}h` : "—"}</div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="mt-5 flex items-center gap-1.5 text-xs text-[#1E2D44]/45"><Icons.Lock size={12} /> To change your details, speak to your manager or people administrator.</p>
    </div>
  );
}
